import React, { useState, useEffect } from 'react'
import axios from 'axios'

const API  = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const TEAL = '#0d9488'
const TEAL_DARK  = '#0f766e'
const TEAL_LIGHT = '#f0fdfa'
const TEAL_MID   = '#99f6e4'
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

const TIME_SLOTS = [
  '7:00 AM','7:30 AM','8:00 AM','8:30 AM','9:00 AM','9:30 AM',
  '10:00 AM','10:30 AM','11:00 AM','11:30 AM','12:00 PM','12:30 PM',
  '1:00 PM','1:30 PM','2:00 PM','2:30 PM','3:00 PM','3:30 PM',
  '4:00 PM','4:30 PM','5:00 PM','5:30 PM','6:00 PM',
]

const STATUS_META = {
  requested:  { label: 'Pending',    bg: '#fefce8', color: '#ca8a04', border: '#fde68a' },
  confirmed:  { label: 'Confirmed',  bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  checked_in: { label: 'Checked In', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  cancelled:  { label: 'Cancelled',  bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  completed:  { label: 'Completed',  bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
}

const todayStr = () => new Date().toISOString().split('T')[0]

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.requested
  return (
    <span style={{
      padding: '2px 9px', borderRadius: '99px', fontSize: '11px', fontWeight: '600',
      background: m.bg, color: m.color, border: `1px solid ${m.border}`,
    }}>{m.label}</span>
  )
}

function Appointments({ clinicId, userRole, isDemo }) {
  const [appointments, setAppointments] = useState([])
  const [patients, setPatients]         = useState([])
  const [filter, setFilter]             = useState('today')
  const [showBook, setShowBook]         = useState(false)
  const [bookForm, setBookForm]         = useState({
    patient_id: '', appointment_date: todayStr(), appointment_time: '9:00 AM', notes: '',
  })
  const [saving, setSaving] = useState(false)
  const [patientSearch, setPatientSearch] = useState('')

  const isFrontDesk = userRole === 'front_desk'

  useEffect(() => {
    if (!clinicId && !isDemo) return
    fetchAppointments()
    const interval = setInterval(fetchAppointments, 15000)
    return () => clearInterval(interval)
  }, [clinicId, isDemo])

  useEffect(() => {
    if (!clinicId && !isDemo) return
    const url = clinicId
      ? API + '/patients?clinic_id=' + clinicId
      : API + '/patients?demo=true'
    axios.get(url)
      .then(res => setPatients(res.data || []))
      .catch(() => {})
  }, [clinicId, isDemo])

  const fetchAppointments = () => {
    axios.get(API + '/appointments' + (clinicId ? '?clinic_id=' + clinicId : ''))
      .then(res => setAppointments(res.data))
      .catch(err => console.error(err))
  }

  const handleConfirm  = (id) => axios.put(API + '/appointments/' + id + '/confirm').then(fetchAppointments)
  const handleCheckIn  = (id) => axios.put(API + '/appointments/' + id + '/check-in').then(fetchAppointments)
  const handleCancel   = (id) => { if (!window.confirm('Cancel this appointment?')) return; axios.put(API + '/appointments/' + id + '/cancel').then(fetchAppointments) }
  const handleDelete   = (id) => { if (!window.confirm('Delete this appointment permanently?')) return; axios.delete(API + '/appointments/' + id).then(fetchAppointments) }

  const handleBook = () => {
    if (!bookForm.patient_id || !bookForm.appointment_date || !bookForm.appointment_time) return
    setSaving(true)
    axios.post(API + '/appointments', {
      patient_id: parseInt(bookForm.patient_id),
      appointment_date: bookForm.appointment_date,
      appointment_time: bookForm.appointment_time,
      notes: bookForm.notes,
      clinic_id: clinicId,
      status: 'confirmed',   // staff-booked = auto confirmed
    }).then(() => {
      fetchAppointments()
      setShowBook(false)
      setBookForm({ patient_id: '', appointment_date: todayStr(), appointment_time: '9:00 AM', notes: '' })
      setPatientSearch('')
    }).catch(() => {}).finally(() => setSaving(false))
  }

  // Filter logic
  const today = todayStr()
  const filtered = appointments.filter(a => {
    if (filter === 'today')      return a.appointment_date === today
    if (filter === 'upcoming')   return a.appointment_date > today && a.status !== 'cancelled'
    if (filter === 'pending')    return a.status === 'requested'
    if (filter === 'checked_in') return a.status === 'checked_in'
    if (filter === 'all')        return true
    return a.status === filter
  })

  const pendingCount   = appointments.filter(a => a.status === 'requested').length
  const todayCount     = appointments.filter(a => a.appointment_date === today && a.status !== 'cancelled').length
  const checkedInCount = appointments.filter(a => a.status === 'checked_in').length

  // Patient search for book form
  const filteredPatients = patients.filter(p =>
    p.name?.toLowerCase().includes(patientSearch.toLowerCase())
  ).slice(0, 8)

  const inp = {
    padding: '9px 12px', fontSize: '14px', border: '1.5px solid rgba(124,58,237,0.25)',
    borderRadius: '8px', width: '100%', fontFamily: FONT, color: '#0f172a',
    outline: 'none', boxSizing: 'border-box',
  }

  return (
    <div style={{ fontFamily: FONT }}>
      <style>{`
        @media (min-width: 1440px) {
          .appt-book-grid { grid-template-columns: 1fr 1fr 1fr !important; }
          .appt-card { padding: 18px 24px !important; }
        }
        @media (max-width: 640px) {
          .appt-header { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; }
          .appt-book-grid { grid-template-columns: 1fr !important; }
          .appt-card { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
          .appt-card-actions { width: 100% !important; justify-content: flex-start !important; }
        }
      `}</style>

      {/* Header */}
      <div className="appt-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Appointments</h2>
          <p style={{ color: '#64748b', fontSize: '14px', margin: '4px 0 0' }}>
            {todayCount} today · {checkedInCount} checked in · {pendingCount > 0 ? `${pendingCount} pending approval` : 'no pending requests'}
          </p>
        </div>
        <button onClick={() => setShowBook(v => !v)} style={{
          padding: '9px 18px', background: TEAL, color: 'white', border: 'none',
          borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: FONT,
          display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          <span style={{ fontSize: '18px', lineHeight: 1 }}>+</span> Schedule Appointment
        </button>
      </div>

      {/* Book Appointment Form */}
      {showBook && (
        <div style={{ background: TEAL_LIGHT, border: `1.5px solid ${TEAL_MID}`, borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: TEAL_DARK, marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Schedule Appointment
          </div>
          <div className="appt-book-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            {/* Patient search */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Patient</label>
              <input
                placeholder="Search patient by name..."
                value={patientSearch}
                onChange={e => { setPatientSearch(e.target.value); setBookForm(f => ({ ...f, patient_id: '' })) }}
                style={inp}
              />
              {patientSearch && !bookForm.patient_id && (
                <div className="cp-card" style={{ background: 'rgba(255,255,255,0.97)', borderRadius: '8px', marginTop: '4px', overflow: 'hidden' }}>
                  {filteredPatients.length === 0
                    ? <div style={{ padding: '10px 14px', fontSize: '13px', color: '#94a3b8' }}>No patients found</div>
                    : filteredPatients.map(p => (
                      <div key={p.id} onClick={() => { setBookForm(f => ({ ...f, patient_id: p.id })); setPatientSearch(p.name) }}
                        style={{ padding: '10px 14px', fontSize: '14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', color: '#0f172a' }}
                        onMouseEnter={e => e.target.style.background = '#f0fdfa'}
                        onMouseLeave={e => e.target.style.background = 'white'}
                      >
                        <strong>{p.name}</strong> <span style={{ color: '#94a3b8', fontSize: '12px' }}>{p.injury_type}</span>
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Date</label>
              <input type="date" value={bookForm.appointment_date}
                onChange={e => setBookForm(f => ({ ...f, appointment_date: e.target.value }))} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Time</label>
              <select value={bookForm.appointment_time}
                onChange={e => setBookForm(f => ({ ...f, appointment_time: e.target.value }))} style={inp}>
                {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Notes (optional)</label>
              <input placeholder="e.g. Follow-up, initial eval, re-assessment..." value={bookForm.notes}
                onChange={e => setBookForm(f => ({ ...f, notes: e.target.value }))} style={inp} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleBook} disabled={saving || !bookForm.patient_id} style={{
              padding: '9px 20px', background: bookForm.patient_id ? TEAL : '#94a3b8',
              color: 'white', border: 'none', borderRadius: '8px', cursor: bookForm.patient_id ? 'pointer' : 'default',
              fontSize: '14px', fontWeight: '600', fontFamily: FONT,
            }}>{saving ? 'Saving…' : 'Book Appointment'}</button>
            <button onClick={() => { setShowBook(false); setPatientSearch('') }} style={{
              padding: '9px 16px', background: 'white', color: '#64748b', border: '1.5px solid rgba(124,58,237,0.25)',
              borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontFamily: FONT,
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[
          { id: 'today',      label: `Today (${todayCount})` },
          { id: 'upcoming',   label: 'Upcoming' },
          { id: 'pending',    label: `Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
          { id: 'checked_in', label: `Checked In${checkedInCount > 0 ? ` (${checkedInCount})` : ''}` },
          { id: 'all',        label: 'All' },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} style={{
            padding: '7px 14px', borderRadius: '8px',
            border: '1.5px solid ' + (filter === f.id ? TEAL : '#e2e8f0'),
            background: filter === f.id ? TEAL : 'white',
            color: filter === f.id ? 'white' : '#64748b',
            cursor: 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: FONT,
          }}>{f.label}</button>
        ))}
      </div>

      {/* Appointment list */}
      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '15px', padding: '48px', background: 'rgba(255,255,255,0.78)', borderRadius: '10px', border: '1.5px solid rgba(13,148,136,0.2)' }}>
          {filter === 'today' ? 'No appointments scheduled for today.' : 'No appointments found.'}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {filtered.map(a => {
          const meta = STATUS_META[a.status] || STATUS_META.requested
          return (
            <div key={a.id} className="appt-card cp-card" style={{
              background: 'rgba(255,255,255,0.92)',
              borderRadius: '10px', padding: '14px 18px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a' }}>{a.patient_name}</span>
                  {a.injury_type && <span style={{ fontSize: '12px', color: '#94a3b8' }}>{a.injury_type}</span>}
                  <StatusBadge status={a.status} />
                </div>
                <div style={{ fontSize: '14px', color: TEAL, fontWeight: '500' }}>
                  {a.appointment_date} · {a.appointment_time}
                </div>
                {a.notes && <div style={{ fontSize: '13px', color: '#64748b', marginTop: '3px' }}>{a.notes}</div>}
              </div>

              <div className="appt-card-actions" style={{ display: 'flex', gap: '6px', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {/* Confirm pending requests */}
                {a.status === 'requested' && (
                  <>
                    <button onClick={() => handleConfirm(a.id)} style={{ padding: '6px 12px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: FONT }}>Confirm</button>
                    <button onClick={() => handleCancel(a.id)} style={{ padding: '6px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontFamily: FONT }}>Decline</button>
                  </>
                )}
                {/* Check In — for confirmed appointments on today's date */}
                {a.status === 'confirmed' && a.appointment_date === today && (
                  <button onClick={() => handleCheckIn(a.id)} style={{ padding: '6px 12px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: FONT }}>
                    Check In
                  </button>
                )}
                {/* Cancel confirmed */}
                {(a.status === 'confirmed' || a.status === 'checked_in') && (
                  <button onClick={() => handleCancel(a.id)} style={{ padding: '6px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontFamily: FONT }}>Cancel</button>
                )}
                <button onClick={() => handleDelete(a.id)} style={{ padding: '6px 10px', background: '#f8fafc', color: '#94a3b8', border: '1.5px solid rgba(13,148,136,0.2)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontFamily: FONT }}>Delete</button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default Appointments
